import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { playerGameStatusLabels } from '@sorare/core/src/lib/glossary';
import { Color } from '@sorare/core/src/style/types';

import type { ScoresBreakdown_averageLastScore } from './__generated__/index.graphql';

type Props = {
  averageLastScore: Nullable<ScoresBreakdown_averageLastScore>;
};

enum Threshold {
  ZERO_TO_TWENTY = 'ZERO_TO_TWENTY',
  TWENTY_TO_FORTY = 'TWENTY_TO_FORTY',
  FORTY_TO_SIXTY = 'FORTY_TO_SIXTY',
  SIXTY_TO_EIGHTY = 'SIXTY_TO_EIGHTY',
  EIGHTY_TO_NINETY = 'EIGHTY_TO_NINETY',
  NINETY_TO_HUNDRED = 'NINETY_TO_HUNDRED',
}

const DNP = 'DNP' as const;
type ThresholdOrDNP = Threshold | typeof DNP;

const thresholdSkeleton: {
  [K in ThresholdOrDNP]: {
    min?: number;
    max: number;
    color: Color;
  };
} = {
  [DNP]: {
    max: 0,
    color: 'var(--c-nd-150)',
  },
  [Threshold.ZERO_TO_TWENTY]: {
    min: 0,
    max: 20,
    color: 'var(--c-score-veryLow)',
  },
  [Threshold.TWENTY_TO_FORTY]: {
    min: 20,
    max: 40,
    color: 'var(--c-score-low)',
  },
  [Threshold.FORTY_TO_SIXTY]: {
    min: 40,
    max: 60,
    color: 'var(--c-score-mediumLow)',
  },
  [Threshold.SIXTY_TO_EIGHTY]: {
    min: 60,
    max: 80,
    color: 'var(--c-score-medium)',
  },
  [Threshold.EIGHTY_TO_NINETY]: {
    min: 80,
    max: 90,
    color: 'var(--c-score-mediumHigh)',
  },
  [Threshold.NINETY_TO_HUNDRED]: {
    min: 90,
    max: 100,
    color: 'var(--c-score-high)',
  },
};

const getThreshold = (score: number | null): ThresholdOrDNP => {
  if (score === null) return DNP;
  for (const [threshold, { max }] of Object.entries(thresholdSkeleton)) {
    if (score <= max) return threshold as Threshold;
  }
  return Threshold.NINETY_TO_HUNDRED;
};

const Root = styled(Vertical)`
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  border: 1px solid var(--color-nd-150);
`;

const ProgressBar = styled(Horizontal)`
  background-color: var(--c-nd-100);
  height: var(--triple-unit);
  gap: 2px;
  border-radius: var(--unit);
`;

const ProgressBarItem = styled.div`
  height: var(--triple-unit);
  transition: all 0.3s;
  &:first-child {
    border-top-left-radius: var(--unit);
    border-bottom-left-radius: var(--unit);
  }
  &:last-child {
    border-top-right-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;

const Bullet = styled.div`
  width: var(--unit);
  height: var(--unit);
  border-radius: 50%;
`;
const Label = styled(Horizontal)`
  flex-grow: 1;
`;

export const ScoresBreakdown = ({ averageLastScore }: Props) => {
  const totalScores = averageLastScore?.lastIndividualScores.length || 0;

  const scoresCountByThreshold = (
    averageLastScore?.lastIndividualScores || []
  ).reduce<{ [K in ThresholdOrDNP]?: number }>((acc, score) => {
    if (score) {
      const threshold = getThreshold(score);
      acc[threshold] = (acc[threshold] || 0) + 1;
    } else {
      acc[DNP] = (acc[DNP] || 0) + 1;
    }
    return acc;
  }, {});

  const scores = Object.entries(thresholdSkeleton).map(([i, score]) => ({
    ...score,
    value: i,
    count: scoresCountByThreshold[i as ThresholdOrDNP] || 0,
  }));

  const reversedScores = [...scores].reverse();

  return (
    <Root>
      <ProgressBar>
        {scores.map(
          score =>
            score.count > 0 && (
              <ProgressBarItem
                key={score.value}
                style={{
                  backgroundColor: score.color,
                  width: `${(score.count / totalScores) * 100}%`,
                }}
              />
            )
        )}
      </ProgressBar>
      <Vertical>
        {reversedScores.map(score => (
          <Horizontal key={score.value}>
            <Label>
              <Bullet style={{ backgroundColor: score.color }} />
              <LabelL>
                {score.value === DNP ? (
                  <FormattedMessage {...playerGameStatusLabels.did_not_play} />
                ) : (
                  <FormattedMessage
                    id="ScoresBreakdown.label"
                    defaultMessage="{min} to {max}"
                    values={{
                      min: score.min,
                      max: score.max,
                    }}
                  />
                )}
              </LabelL>
            </Label>
            <LabelL bold>{score.count}</LabelL>
          </Horizontal>
        ))}
      </Vertical>
    </Root>
  );
};

ScoresBreakdown.fragments = {
  averageLastScore: gql`
    fragment ScoresBreakdown_averageLastScore on AverageLastScore {
      id
      lastIndividualScores
    }
  ` as TypedDocumentNode<ScoresBreakdown_averageLastScore>,
};
